import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from "@mui/material";
import { DialogHeader } from "../../../../auth/components/DialogHeader";
import DatePickerLocalizationAdapter from "../../../../main/context/DatePickerLocalizationAdapter";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { operationsApi } from "../../../../services/api";
import { MainContext } from "../../../../main/context/MainContextProvider";
import CardBox from "../../../../main/components/CardBox";
import CheckBoxTableRow from "../../../users/components/CheckBoxTableRow";
import { UsinaByClient } from "../../../usinas/types";

export default function ReprocessTacticalModal(props: {
    setOpen: (open: boolean) => void;
    open: boolean;
    usinas: UsinaByClient[];
    onCompleted: () => void;
}) {
    const { toastNotifications, setToastNotifications } = useContext(MainContext);
    const { open, setOpen, usinas, onCompleted } = props;
    const onClose = () => setOpen(false);

    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [powerPlantIds, setPowerPlantIds] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setPowerPlantIds(usinas.map((usina) => usina.usinaIds.id));
            setSelectedDate(dayjs());
        }
    }, [open, usinas]);

    const removePowerPlantId = (powerPlantId: string) => {
        setPowerPlantIds((current) => current.filter((id) => id !== powerPlantId));
    };

    const reprocessTactical = async () => {
        if (!selectedDate || powerPlantIds.length === 0) return;

        const correctionDate = selectedDate.format("YYYY-MM-DD");
        setSubmitting(true);
        onClose();
        setToastNotifications({
            isAdding: true,
            notifications: [
                ...toastNotifications.notifications,
                {
                    severity: "info" as const,
                    text: "O reprocessamento do Tático foi iniciado.",
                },
            ],
        });

        try {
            const response = await operationsApi.reprocessTactical(
                correctionDate,
                powerPlantIds,
            );
            const failedCount = response.failedDates?.length ?? 0;
            setToastNotifications({
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: failedCount > 0 ? "warning" : "success",
                        text:
                            failedCount > 0
                                ? `Reprocessamento concluído com falha em ${failedCount} dia(s).`
                                : `Dados do Tático reprocessados de ${selectedDate.format("DD/MM/YYYY")} até hoje.`,
                    },
                ],
            });
            onCompleted();
        } catch (error) {
            console.error(error);
            setToastNotifications({
                isAdding: true,
                notifications: [
                    ...toastNotifications.notifications,
                    {
                        severity: "error" as const,
                        text: "Erro ao reprocessar os dados do Tático. Tente novamente.",
                    },
                ],
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            onClose={onClose}
            aria-labelledby="reprocess-tactical-dialog"
            open={open}
            sx={{
                "& .MuiDialog-paper": {
                    width: "100%",
                    maxWidth: "400px",
                    maxHeight: "600px",
                },
            }}
        >
            <DialogHeader id="reprocess-tactical-dialog" onClose={onClose}>
                <Typography
                    sx={{
                        fontSize: { xs: "1rem", sm: "1.5rem" },
                        fontWeight: 500,
                    }}
                >
                    Reprocessar Tático
                </Typography>
            </DialogHeader>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    overflowY: "auto",
                }}
                dividers
            >
                {submitting ? (
                    <Box sx={{ textAlign: "center", height: "100px" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography
                            sx={{
                                fontWeight: 400,
                                fontSize: "13px",
                                marginBottom: "12px",
                                color: "#CFD8DC",
                            }}
                        >
                            Informe a data da correção. A persistência das usinas MQTT
                            será regravada dessa data até hoje. Usinas do DW não entram
                            neste reprocessamento.
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                columnGap: "10px",
                                alignItems: "center",
                                width: "100%",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
                                Usinas
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                                    Selecionar Todos
                                </Typography>
                                <CheckBoxTableRow
                                    active={usinas.length > 0 && usinas.length === powerPlantIds.length}
                                    onChange={(_, checked) =>
                                        checked
                                            ? setPowerPlantIds(usinas.map((usina) => usina.usinaIds.id))
                                            : setPowerPlantIds([])
                                    }
                                />
                            </Box>
                        </Box>
                        <CardBox
                            sx={{
                                width: "100%",
                                height: "200px",
                                maxHeight: "200px",
                                overflow: "auto",
                            }}
                        >
                            <TableContainer sx={{ display: "flex", alignItems: "flex-start", height: "100%" }}>
                                <Table size="small" stickyHeader>
                                    <TableBody>
                                        {usinas.map((usina) => (
                                            <TableRow key={usina.usinaIds.id} sx={{ display: "flex", width: "100%" }}>
                                                <TableCell
                                                    sx={{
                                                        width: "100%",
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                    }}
                                                    align="left"
                                                >
                                                    <Typography sx={{ color: "#FFF" }}>{usina.name}</Typography>
                                                    <CheckBoxTableRow
                                                        active={powerPlantIds.includes(usina.usinaIds.id)}
                                                        onChange={(_, checked) => {
                                                            checked
                                                                ? setPowerPlantIds([...powerPlantIds, usina.usinaIds.id])
                                                                : removePowerPlantId(usina.usinaIds.id);
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardBox>
                        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", marginTop: "18px" }}>
                            <DatePickerLocalizationAdapter>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: "small" } }}
                                    maxDate={dayjs()}
                                    label="Data da correção"
                                    value={selectedDate}
                                    onChange={(date) => {
                                        if (!date) return;
                                        setSelectedDate(date);
                                    }}
                                />
                            </DatePickerLocalizationAdapter>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={submitting || powerPlantIds.length === 0 || !selectedDate}
                    onClick={reprocessTactical}
                >
                    Reprocessar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
